import styled from 'styled-components'

const QrScanStyles = {
	Container: styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 40px;
		padding: 32px 16px;
		background-color: ${({theme}) => theme.color.white};
		min-height: calc(100dvh - 96px);

		@media (max-width: ${({theme}) => theme.breakpoints.mobile}) {
			padding: 24px 16px;
			gap: 32px;
		}
	`,

	Wrapper: styled.div`
		position: relative;
	`,

	SubTitle: styled.h2`
		${({theme}) => theme.font.medium || theme.font.small};
		color: ${({theme}) => theme.color.black || '#1F2024'};
		text-align: center;
		margin: 0;
		font-size: 16px;
		font-weight: 400;
		line-height: 22px;
		letter-spacing: 0%;
	`,

	Title: styled.h1`
		${({theme}) => theme.font.large_bold || theme.font.large};
		color: ${({theme}) => theme.color.black || '#1F2024'};
		text-align: center;
		margin: 0;
		font-size: 20px;
		font-weight: 700;
		line-height: 1.2;

		@media (max-width: ${({theme}) => theme.breakpoints.mobile}) {
			font-size: 18px;
		}
	`,

	QrScanArea: styled.div`
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
	`,

	QrFrame: styled.div`
		position: relative;
		width: 320px;
		height: 320px;
		border-radius: 8px;
		border: 1px solid ${({theme}) => theme.color.black || '#000000'};
		background: linear-gradient(135deg, #eef0f2 0%, #f9fafb 100%);

		&::before {
			content: '';
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			width: 200px;
			height: 200px;
			border: 2px dashed ${({theme}) => theme.color.grey3 || '#D1D5DB'};
			border-radius: 8px;
			opacity: 0.5;
		}

		&::after {
			content: 'QR';
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			font-size: 24px;
			font-weight: 600;
			color: ${({theme}) => theme.color.grey4 || '#6B7280'};
			opacity: 0.3;
		}
		overflow: hidden;

		@media (max-width: 360px) {
			width: 280px;
			height: 280px;
		}
	`,

	VideoContainer: styled.div`
		position: relative;
		width: 100%;
		height: 100%;
		border-radius: 8px;
		overflow: hidden;
		z-index: 2;
		background-color: ${({theme}) => theme.color.black || '#000000'};
	`,

	ScanOverlay: styled.div`
		position: absolute;
		top: 30px;
		left: 30px;
		right: 30px;
		bottom: 30px;
		border: 3px solid ${({theme}) => theme.color.white || '#FFFFFF'};
		border-radius: 8px;
		pointer-events: none;

		&::before {
			content: '';
			position: absolute;
			top: -3px;
			left: -3px;
			right: -3px;
			bottom: -3px;
			border: 1px solid rgba(255, 255, 255, 0.5);
			border-radius: 8px;
		}
	`,

	PlaceholderContent: styled.div`
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 100%;
		background: rgba(238, 240, 242, 0.8);
		position: absolute;
		top: 0;
		left: 0;
		z-index: 3;
		p {
			text-align: center;
		}
	`,

	LoadingOverlay: styled.div`
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.1);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 4;
		border-radius: 8px;
	`,

	LoadingContent: styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 16px;
		color: ${({theme}) => theme.color.white || '#FFFFFF'};
		font-size: 16px;
		font-weight: 500;
	`,

	LoadingSpinner: styled.div`
		width: 40px;
		height: 40px;
		border: 3px solid rgba(255, 255, 255, 0.3);
		border-top: 3px solid ${({theme}) => theme.color.white || '#FFFFFF'};
		border-radius: 50%;
		animation: spin 1s linear infinite;

		@keyframes spin {
			0% {
				transform: rotate(0deg);
			}
			100% {
				transform: rotate(360deg);
			}
		}
	`,

	ScanButton: styled.button`
		${({theme}) => theme.font.small || theme.font.medium};
		background-color: ${({theme}) => theme.color.major || '#006FFD'};
		color: ${({theme}) => theme.color.white || '#FFFFFF'};
		border: none;
		border-radius: 8px;
		padding: 16px 32px;
		cursor: pointer;
		transition: all 0.2s ease;

		&:hover {
			background-color: ${({theme}) => theme.color.major_dark || '#0056CC'};
			transform: translateY(-1px);
		}

		&:active {
			transform: translateY(0);
		}
	`,

	SubmitButton: styled.button`
		${({theme}) => theme.font.small || theme.font.medium};
		background-color: ${({theme}) => theme.color.success || '#10B981'};
		color: ${({theme}) => theme.color.white || '#FFFFFF'};
		border: none;
		border-radius: 8px;
		padding: 16px 32px;
		cursor: pointer;
		transition: all 0.2s ease;
		min-width: 140px;

		&:hover:not(:disabled) {
			background-color: ${({theme}) => theme.color.success_dark || '#059669'};
			transform: translateY(-1px);
		}

		&:active:not(:disabled) {
			transform: translateY(0);
		}

		&:disabled {
			background-color: ${({theme}) => theme.color.grey3 || '#D1D5DB'};
			cursor: not-allowed;
			transform: none;
		}
	`,

	ControlButtons: styled.div`
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
		z-index: 10;
		width: fit-content;
	`,

	StopButton: styled.button`
		${({theme}) => theme.font.extra_small || theme.font.small};
		background-color: ${({theme}) => theme.color.danger || '#ED3241'};
		color: ${({theme}) => theme.color.white || '#FFFFFF'};
		border: none;
		border-radius: 8px;
		padding: 12px 24px;
		cursor: pointer;
		transition: all 0.2s ease;

		&:hover {
			background-color: ${({theme}) => theme.color.danger_dark || '#CC1E2C'};
			transform: translateY(-1px);
		}

		&:active {
			transform: translateY(0);
		}
	`,

	InstructionsContainer: styled.div`
		display: flex;
		flex-direction: column;
		gap: 8px;
		width: 100%;
		max-width: 400px;
		align-self: stretch;
		margin: 0 auto;

		@media (max-width: ${({theme}) => theme.breakpoints.mobile}) {
			max-width: none;
		}
	`,

	SectionTitle: styled.h2`
		${({theme}) => theme.font.small_bold || theme.font.small};
		color: ${({theme}) => theme.color.black || '#1F2024'};
		margin: 0;
	`,

	InstructionsList: styled.ul`
		${({theme}) => theme.font.small || theme.font.medium};
		color: ${({theme}) => theme.color.black || '#1F2024'};
		margin: 0;
		padding: 0;
		list-style: none;
	`,

	InstructionsCenterList: styled.p`
		${({theme}) => theme.font.small || theme.font.medium};
		color: ${({theme}) => theme.color.black || '#1F2024'};
		margin: 0;
		padding: 0;
		text-align: center;
	`,

	InstructionItem: styled.li`
		position: relative;
		padding-left: 16px;
		margin-bottom: 4px;

		&::before {
			content: '•';
			position: absolute;
			left: 0;
			color: ${({theme}) => theme.color.black || '#1F2024'};
		}

		&:last-child {
			margin-bottom: 0;
		}
	`,

	InstructionItemCenter: styled.p`
		${({theme}) => theme.font.medium || theme.font.small};
		font-size: 16px;
		position: relative;
		margin-bottom: 4px;
		text-align: center;
		word-break: keep-all;
		overflow-wrap: break-word;
		line-height: 22px;
		letter-spacing: 0%;
	`,

	ErrorContainer: styled.div`
		display: flex;
		justify-content: center;
		width: 100%;
		max-width: 400px;
		padding: 12px 16px;
		background-color: ${({theme}) => theme.color.danger_light || '#FEE2E2'};
		border: 1px solid ${({theme}) => theme.color.danger || '#ED3241'};
		border-radius: 8px;

		@media (max-width: ${({theme}) => theme.breakpoints.mobile}) {
			max-width: none;
		}
	`,

	ErrorText: styled.p`
		${({theme}) => theme.font.extra_small || theme.font.small};
		color: ${({theme}) => theme.color.danger || '#ED3241'};
		margin: 0;
		text-align: center;
	`,

	ResultContainer: styled.div`
		display: flex;
		flex-direction: column;
		gap: 8px;
		width: 100%;
		max-width: 400px;
		padding: 16px;
		background-color: ${({theme}) => theme.color.grey1 || '#EEF0F2'};
		border-radius: 8px;

		@media (max-width: ${({theme}) => theme.breakpoints.mobile}) {
			max-width: none;
		}
	`,

	ResultHeader: styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 4px;
	`,

	ClearButton: styled.button`
		${({theme}) => theme.font.extra_small || theme.font.small};
		background-color: ${({theme}) => theme.color.white || '#FFFFFF'};
		color: ${({theme}) => theme.color.grey4 || '#6B7280'};
		border: 1px solid ${({theme}) => theme.color.grey3 || '#D1D5DB'};
		border-radius: 4px;
		padding: 6px 12px;
		cursor: pointer;
		transition: all 0.2s ease;
		font-size: 14px;
		display: flex;
		align-items: center;
		gap: 4px;

		&:hover {
			background-color: ${({theme}) => theme.color.grey2 || '#F3F4F6'};
			color: ${({theme}) => theme.color.black || '#1F2024'};
		}

		&:active {
			transform: translateY(1px);
		}
	`,

	ScannedData: styled.div`
		${({theme}) => theme.font.extra_small || theme.font.small};
		color: ${({theme}) => theme.color.black || '#1F2024'};
		background-color: ${({theme}) => theme.color.white || '#FFFFFF'};
		padding: 12px;
		border-radius: 4px;
		border: 1px solid ${({theme}) => theme.color.grey2 || '#D1D5DB'};
		word-break: break-all;
		font-family: monospace;
	`,

	ScannedDataContainer: styled.div`
		display: flex;
		flex-direction: column;
		gap: 8px;
		width: 100%;
		max-width: 400px;
		padding: 16px;
		background-color: ${({theme}) => theme.color.success_light || '#D1FAE5'};
		border: 1px solid ${({theme}) => theme.color.success || '#10B981'};
		border-radius: 8px;

		@media (max-width: ${({theme}) => theme.breakpoints.mobile}) {
			max-width: none;
		}
	`,

	ScannedDataHeader: styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 4px;
	`,

	PlaceholderBox: styled.div`
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		width: 100%;
		max-width: 320px;
		height: 320px;
		border-radius: 8px;
		border: 2px dashed ${({theme}) => theme.color.grey3 || '#B2B2B2'};
		background: linear-gradient(135deg, #f9fafb 0%, #eef0f2 100%);
		gap: 16px;
		position: relative;

		@media (max-width: 360px) {
			width: 280px;
			height: 280px;
		}
	`,

	PlaceholderIcon: styled.div`
		width: 60px;
		height: 60px;
		border-radius: 8px;
		background-color: ${({theme}) => theme.color.grey2 || '#DFDFE7'};
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;

		&::before,
		&::after {
			content: '';
			position: absolute;
			background-color: ${({theme}) => theme.color.grey4 || '#8D8D8D'};
		}

		/* QR code pattern */
		&::before {
			width: 20px;
			height: 20px;
			top: 8px;
			left: 8px;
			border-radius: 2px;
		}

		&::after {
			width: 12px;
			height: 12px;
			bottom: 8px;
			right: 8px;
			border-radius: 1px;
		}
	`,

	PlaceholderText: styled.div`
		text-align: center;
		color: ${({theme}) => theme.color.grey4 || '#8D8D8D'};
	`,

	PlaceholderTitle: styled.h3`
		${({theme}) => theme.font.small_bold || theme.font.small};
		margin: 0;
		font-size: 16px;
		font-weight: 600;
		line-height: 1.2;
		text-align: center;
		margin: 0 auto;
	`,

	PlaceholderSubtitle: styled.p`
		${({theme}) => theme.font.extra_small || theme.font.small};
		margin: 4px 0 0 0;
		font-size: 12px;
		font-weight: 400;
		line-height: 1.2;
		opacity: 0.8;
		text-align: center;
		margin: 0 auto;
	`,
}

export default QrScanStyles
